import React, { useCallback, useContext, useEffect, useState } from "react"
import ContactsTable from "src/main/Contact/ContactsTable"
import { ContactsContext } from "src/main/Contact/index"

import Chips from "src/components/Chips"
import Form from "src/components/Form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "src/components/Select"

import { getContacts } from "src/api/Contacts"

import useDatatable from "src/hooks/use_datatable"
import useDebounce from "src/hooks/use_debounce"

import { capitalize } from "src/utils/string_helpers"

const LINKED_LABELS = {
  linked: "Linked",
  not_linked: "Not linked",
}

const ContactsTab = () => {
  const { canCreate, canExport, exportUrl, newContactUrl } =
    useContext(ContactsContext)

  const table = useDatatable({
    queryKey: "contacts",
    fetchFn: getContacts,
    defaultFilters: {
      search: "",
      status: "active",
      linked: "all",
    },
    idKey: "encodedId",
  })

  const [searchInputValue, setSearchInputValue] = useState(
    table.filters.search || ""
  )
  const [debouncedSetSearch] = useDebounce((value) => {
    table.setFilter("search", value)
  })

  useEffect(() => {
    if (!table.filters.search) setSearchInputValue("")
  }, [table.filters.search])

  const handleSearch = useCallback(
    (e) => {
      const value = e.target.value
      setSearchInputValue(value)
      debouncedSetSearch(value.trim())
    },
    [debouncedSetSearch]
  )

  const handleClearSearch = useCallback(() => {
    setSearchInputValue("")
    table.clearFilter("search")
  }, [table])

  const handleReset = useCallback(() => {
    table.clearAllFilters()
    setSearchInputValue("")
  }, [table])

  const linkedCounts = table.data?.filterOptions?.linked

  const renderContactSearch = () => (
    <Form.IconTextField
      icon={
        <i className="fa-solid fa-magnifying-glass text-xl text-gray-400" />
      }
      id="contact-search"
      isClearable
      onChange={handleSearch}
      onClearSelection={handleClearSearch}
      position="left"
      value={searchInputValue}
      placeholder="Search for a captain"
    />
  )

  const renderArchiveStatusFilter = () => (
    <Select
      value={table.filters.status}
      onValueChange={(value) => table.setFilter("status", value || "active")}
    >
      <SelectTrigger variant="button" className="w-full" aria-label="Status">
        {capitalize(table.filters.status)}
      </SelectTrigger>
      <SelectContent className="max-h-none">
        <SelectItem value="active">Active</SelectItem>
        <SelectItem value="archived">Archived</SelectItem>
      </SelectContent>
    </Select>
  )

  const renderLinkedFilter = () => (
    <Select
      value={table.filters.linked}
      onValueChange={(value) => table.setFilter("linked", value || "all")}
    >
      <SelectTrigger
        variant="button"
        className="w-full"
        aria-label="Dockwa account"
      >
        Dockwa account
      </SelectTrigger>
      <SelectContent className="max-h-none">
        <SelectItem value="all">All ({linkedCounts?.all ?? 0})</SelectItem>
        <SelectItem value="linked">
          Linked ({linkedCounts?.linked ?? 0})
        </SelectItem>
        <SelectItem value="not_linked">
          Not linked ({linkedCounts?.notLinked ?? 0})
        </SelectItem>
      </SelectContent>
    </Select>
  )

  const buildChips = () => {
    const chips = []

    if (table.filters.search) {
      chips.push({
        key: "search",
        text: `Search: ${table.filters.search}`,
        onClick: handleClearSearch,
      })
    }

    if (table.filters.linked !== "all") {
      chips.push({
        key: "linked",
        text: `Dockwa account: ${LINKED_LABELS[table.filters.linked]}`,
        onClick: () => table.clearFilter("linked"),
      })
    }

    return chips
  }

  const chips = buildChips()

  return (
    <div>
      <div className="mt-2 flex flex-col justify-between px-4 pb-6 lg:p-0 xl:flex-row print:hidden">
        <div className="mb-2 mr-2 w-full xl:m-0 xl:w-[336px]">
          {renderContactSearch()}
        </div>
        <div className="flex space-x-2 xl:justify-between">
          {renderArchiveStatusFilter()}
          {renderLinkedFilter()}
          {canExport && (
            <a
              href={`${exportUrl}?search=${encodeURIComponent(
                table.filters.search
              )}&status=${encodeURIComponent(
                table.filters.status
              )}&linked=${encodeURIComponent(table.filters.linked)}`}
              className="btn btn-secondary w-full"
            >
              Export
            </a>
          )}
          {canCreate && (
            <a href={newContactUrl} className="btn btn-primary w-full">
              Add new
            </a>
          )}
        </div>
      </div>
      {chips.length > 0 && (
        <div className="px-4 xl:px-0 print:hidden">
          <Chips onReset={handleReset}>
            {chips.map((chip) => (
              <Chips.Chip
                key={chip.key}
                text={chip.text}
                onClick={chip.onClick}
              />
            ))}
          </Chips>
        </div>
      )}
      <div className="mt-4 overflow-x-auto pb-24">
        <ContactsTable table={table} status={table.filters.status} />
      </div>
    </div>
  )
}

export default ContactsTab
